import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  Truck, ChevronDown, Eye, MapPin, ArrowRight,
  Package, CalendarDays, User, Hash,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── Status badge ── */
function StatusBadge({ status }) {
  const cls =
    status === "Completed"  ? "bg-green-50 text-green-700 border-green-200"  :
    status === "In Transit" ? "bg-blue-50 text-blue-700 border-blue-200"     :
                              "bg-violet-50 text-violet-700 border-violet-200";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${cls}`}>
      {status || "Scheduled"}
    </span>
  );
}

/* ── Transfer detail dialog ── */
function TransferDetailDialog({ open, onClose, transfer }) {
  if (!transfer) return null;

  const loads = transfer.load_details || [];

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl rounded-none p-0 gap-0">
        {/* Header */}
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
          <DialogTitle className="text-white font-bold tracking-wide text-base">
            Transfer Details
          </DialogTitle>
          <p className="text-white/60 text-xs mt-0.5">
            {transfer.transfer_date
              ? format(new Date(transfer.transfer_date + "T00:00:00"), "dd MMM yyyy")
              : "—"}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Route */}
          <div className="flex items-center gap-2 py-3 px-4 bg-[#F8FAFC] border border-[#E2E8F0]">
            <MapPin size={14} className="text-[#1B2A6B] shrink-0" />
            <span className="font-semibold text-[#0F172A] text-sm">{transfer.origin_label}</span>
            <ArrowRight size={14} className="text-[#CBD5E1] shrink-0" />
            <span className="font-semibold text-[#0F172A] text-sm">{transfer.destination_label}</span>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Customer */}
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Customer</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <User size={12} className="text-[#94A3B8]" />
                <span>{transfer.customer_name || "—"}</span>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Status</p>
              <StatusBadge status={transfer.status} />
            </div>

            {/* Vehicle */}
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Vehicle</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <Truck size={12} className="text-[#94A3B8]" />
                <span>{transfer.vehicle_display || "—"}</span>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Items</p>
              <div className="flex items-center gap-1.5 text-sm text-[#0F172A]">
                <Package size={12} className="text-[#94A3B8]" />
                <span>{(transfer.load_ids || []).length} item{(transfer.load_ids || []).length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>

          {/* Load list */}
          {loads.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#1B2A6B]">Items Transferred</p>
              <div className="border border-[#E2E8F0] divide-y divide-[#F1F5F9]">
                {loads.map((l, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-[#F8FAFC] text-sm">
                    <span className="font-mono text-xs text-[#1B2A6B] font-bold w-20 shrink-0">{l.load_ref || `#${i + 1}`}</span>
                    <span className="text-[#0F172A]">{l.load_name || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] flex justify-end">
          <Button
            onClick={onClose}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700]"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main page ── */
export default function VehicleReportPage() {
  const [vehicles, setVehicles]             = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [transfers, setTransfers]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [viewTransfer, setViewTransfer]     = useState(null);

  /* Load vehicles list */
  useEffect(() => {
    axios.get(`${API}/vehicles`, { withCredentials: true })
      .then(r => setVehicles(r.data))
      .catch(() => toast.error("Failed to load vehicles."));
  }, []);

  /* Fetch transfers when vehicle is selected */
  const loadReport = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDropdown(false);
    setTransfers([]);
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/reporting/vehicle/${vehicle.id}`,
        { withCredentials: true }
      );
      setTransfers(data.transfers || []);
    } catch {
      toast.error("Failed to load vehicle report.");
    } finally {
      setLoading(false);
    }
  };

  /* Group transfers by date */
  const groupedByDate = useMemo(() => {
    const groups = {};
    for (const t of transfers) {
      const date = t.transfer_date || "Unknown Date";
      if (!groups[date]) groups[date] = [];
      groups[date].push(t);
    }
    /* Sort dates ascending */
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [transfers]);

  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr + "T00:00:00"), "EEEE, dd MMM yyyy"); }
    catch { return dateStr; }
  };

  return (
    <div className="space-y-5" data-testid="vehicle-report-page">

      {/* Page header */}
      <div>
        <h2 className="text-xl font-bold text-[#1B2A6B]">Vehicle Report</h2>
        <p className="text-xs text-[#475569] mt-0.5">View movement history for any vehicle</p>
      </div>

      {/* Vehicle selector */}
      <div className="relative inline-block">
        <button
          onClick={() => setShowDropdown(v => !v)}
          className="flex items-center gap-2 px-4 h-10 border border-[#E2E8F0] bg-white text-sm font-semibold text-[#0F172A] hover:border-[#1B2A6B] transition-colors min-w-[240px]"
        >
          <Truck size={15} className="text-[#1B2A6B] shrink-0" />
          <span className="flex-1 text-left truncate">
            {selectedVehicle
              ? `${selectedVehicle.vehicle_name} · ${selectedVehicle.vehicle_number}`
              : "Select Vehicle"}
          </span>
          <ChevronDown size={14} className="text-[#94A3B8] shrink-0" />
        </button>

        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 min-w-full w-72 bg-white border border-[#E2E8F0] shadow-lg z-30">
            {vehicles.length === 0 ? (
              <p className="px-4 py-3 text-sm text-[#94A3B8]">No vehicles found.</p>
            ) : (
              vehicles.map(v => (
                <button
                  key={v.id}
                  onClick={() => loadReport(v)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-[#F1F5F9] transition-colors border-b border-[#F1F5F9] last:border-b-0 ${
                    selectedVehicle?.id === v.id ? "bg-[#EEF2FF] text-[#1B2A6B]" : "text-[#0F172A]"
                  }`}
                >
                  <p className="font-semibold text-sm">{v.vehicle_name}</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{v.vehicle_number} · {v.vehicle_type}</p>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Click-away */}
      {showDropdown && (
        <div className="fixed inset-0 z-20" onClick={() => setShowDropdown(false)} />
      )}

      {/* No vehicle selected */}
      {!selectedVehicle && !loading && (
        <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 border-2 border-dashed border-[#1B2A6B]/20 rounded-full flex items-center justify-center">
            <Truck size={28} className="text-[#CBD5E1]" />
          </div>
          <p className="text-sm text-[#94A3B8] font-medium">Select a vehicle above to view its movement history</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border border-[#E2E8F0] flex items-center justify-center py-24 text-sm text-[#94A3B8]">
          Loading movements...
        </div>
      )}

      {/* Report */}
      {selectedVehicle && !loading && (
        <>
          {/* Summary strip */}
          <div className="flex flex-wrap items-center gap-6 bg-[#1B2A6B] px-6 py-3 border-b-4 border-[#FFD700]">
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Vehicle</p>
              <p className="text-white font-bold text-sm mt-0.5">{selectedVehicle.vehicle_name}</p>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Reg. Number</p>
              <p className="text-[#FFD700] font-bold text-sm mt-0.5 font-mono">{selectedVehicle.vehicle_number}</p>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Type</p>
              <p className="text-white font-bold text-sm mt-0.5">{selectedVehicle.vehicle_type}</p>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Total Transfers</p>
              <p className="text-[#FFD700] font-bold text-sm mt-0.5">{transfers.length}</p>
            </div>
          </div>

          {/* No transfers */}
          {groupedByDate.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] flex flex-col items-center justify-center py-20 gap-3">
              <CalendarDays size={40} className="text-[#E2E8F0]" />
              <p className="text-sm text-[#94A3B8]">No transfers recorded for this vehicle yet.</p>
            </div>
          ) : (
            /* Date-grouped view */
            <div className="space-y-4">
              {groupedByDate.map(([date, dayTransfers]) => (
                <div key={date} className="bg-white border border-[#E2E8F0]">
                  {/* Date header */}
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-[#F9FAFB] border-b border-[#E2E8F0]">
                    <CalendarDays size={14} className="text-[#1B2A6B] shrink-0" />
                    <span className="text-xs font-bold text-[#1B2A6B] uppercase tracking-widest">
                      {formatDate(date)}
                    </span>
                    <span className="ml-auto text-[10px] text-[#94A3B8] font-semibold">
                      {dayTransfers.length} transfer{dayTransfers.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Transfers table for this date */}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E2E8F0]">
                        {["#", "Route", "Customer", "Items", "Status", ""].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dayTransfers.map((t, idx) => (
                        <tr
                          key={t.id}
                          className={`border-b border-[#E2E8F0] last:border-b-0 ${idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}`}
                        >
                          <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-[#0F172A] whitespace-nowrap">
                              <MapPin size={12} className="text-[#94A3B8] shrink-0" />
                              <span className="max-w-[120px] truncate">{t.origin_label}</span>
                              <ArrowRight size={12} className="text-[#CBD5E1] shrink-0" />
                              <span className="max-w-[120px] truncate">{t.destination_label}</span>
                            </div>
                          </td>

                          <td className="px-4 py-3 text-[#475569] whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <User size={12} className="text-[#94A3B8] shrink-0" />
                              {t.customer_name || "—"}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-[#475569]">
                              <Package size={12} className="text-[#94A3B8] shrink-0" />
                              {(t.load_ids || []).length}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <StatusBadge status={t.status} />
                          </td>

                          <td className="px-4 py-3">
                            <button
                              onClick={() => setViewTransfer(t)}
                              className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#1B2A6B] hover:text-white border border-[#1B2A6B] hover:bg-[#1B2A6B] transition-colors px-3 py-1.5 rounded-sm whitespace-nowrap"
                            >
                              <Eye size={12} />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Transfer detail dialog */}
      <TransferDetailDialog
        open={!!viewTransfer}
        onClose={() => setViewTransfer(null)}
        transfer={viewTransfer}
      />
    </div>
  );
}
